/**
 * Hero image performance instrumentation.
 *
 * Reports the timings that matter for confirming the mobile srcset keeps LCP
 * fast:
 *   - `start`           — when the <picture> element mounts (React commit).
 *   - `resourceStart`   — browser began fetching the chosen candidate.
 *   - `responseEnd`     — bytes finished arriving from the network / cache.
 *   - `load`            — <img> `onload` fired (decode has NOT run yet).
 *   - `decoded`         — `img.decode()` resolved (safe to paint without jank).
 *   - `lcp`             — LargestContentfulPaint entry whose element is this img.
 *
 * All times are `performance.now()` milliseconds relative to
 * `performance.timeOrigin`, so they line up with DevTools' Performance panel.
 *
 * Also emits:
 *   - `console.info("[hero-perf]", report)` for quick tailing.
 *   - `window.dispatchEvent(new CustomEvent("hero-perf", { detail: report }))`
 *     so QA harnesses / analytics can subscribe without importing this module.
 *   - `window.__heroPerf` (last report) for ad-hoc `copy(__heroPerf)` inspection.
 */

export type HeroPerfPhase =
  | "start"
  | "resourceStart"
  | "responseEnd"
  | "load"
  | "decoded"
  | "lcp"
  | "error";

export type HeroPerfReport = {
  /** Rendered src the browser actually loaded (post-srcset resolution). */
  currentSrc: string;
  /** Requested `src` prop (may differ from currentSrc when srcset is used). */
  requestedSrc: string;
  /** Milliseconds since `timeOrigin` for each phase. */
  timings: Partial<Record<HeroPerfPhase, number>>;
  /** Deltas (ms) between key phases — easier to eyeball than absolute times. */
  deltas: {
    /** start → responseEnd (network + browser scheduling). */
    networkMs?: number;
    /** responseEnd → load (browser dispatch). */
    loadMs?: number;
    /** load → decoded (decode cost). */
    decodeMs?: number;
    /** start → decoded (total wall-clock to paintable). */
    totalMs?: number;
    /** start → lcp (contribution to LCP). */
    lcpMs?: number;
  };
  /** Bytes transferred over the wire (0 if served from cache). */
  transferSizeBytes?: number;
  /** Decoded body size on disk (helps validate we picked the mobile variant). */
  encodedBodySizeBytes?: number;
  /** Natural intrinsic pixel dimensions of the chosen candidate. */
  naturalWidth?: number;
  naturalHeight?: number;
  /** CSS pixel dimensions the img renders at. */
  renderedWidth?: number;
  renderedHeight?: number;
  /** Viewport at load time (helpful when comparing mobile vs desktop). */
  viewportWidth: number;
  viewportHeight: number;
  devicePixelRatio: number;
  /** Effective connection info when available (Chromium only). */
  connection?: {
    effectiveType?: string;
    downlinkMbps?: number;
    rttMs?: number;
    saveData?: boolean;
  };
};

declare global {
  interface Window {
    __heroPerf?: HeroPerfReport;
  }
}

/**
 * Attach performance instrumentation to an <img>. Returns a disposer that
 * cleans up all observers — call it on unmount.
 */
export function instrumentHeroImage(
  img: HTMLImageElement,
  opts: { label?: string; requestedSrc: string } = { requestedSrc: "" },
): () => void {
  if (typeof window === "undefined") return () => {};

  const label = opts.label ?? "hero";
  const startedAt = performance.now();
  const timings: HeroPerfReport["timings"] = { start: startedAt };
  let reported = false;
  let resourceObserver: PerformanceObserver | null = null;
  let lcpObserver: PerformanceObserver | null = null;
  let idleHandle: number | null = null;
  let transferSizeBytes: number | undefined;
  let encodedBodySizeBytes: number | undefined;

  const conn: any = (navigator as any).connection;
  const connection = conn
    ? {
        effectiveType: conn.effectiveType,
        downlinkMbps: conn.downlink,
        rttMs: conn.rtt,
        saveData: conn.saveData,
      }
    : undefined;

  const buildReport = (): HeroPerfReport => {
    const total = timings.decoded ?? timings.load;
    return {
      currentSrc: img.currentSrc || img.src,
      requestedSrc: opts.requestedSrc,
      timings: { ...timings },
      deltas: {
        networkMs:
          timings.responseEnd != null
            ? +(timings.responseEnd - startedAt).toFixed(1)
            : undefined,
        loadMs:
          timings.load != null && timings.responseEnd != null
            ? +(timings.load - timings.responseEnd).toFixed(1)
            : undefined,
        decodeMs:
          timings.decoded != null && timings.load != null
            ? +(timings.decoded - timings.load).toFixed(1)
            : undefined,
        totalMs: total != null ? +(total - startedAt).toFixed(1) : undefined,
        lcpMs:
          timings.lcp != null ? +(timings.lcp - startedAt).toFixed(1) : undefined,
      },
      naturalWidth: img.naturalWidth || undefined,
      naturalHeight: img.naturalHeight || undefined,
      renderedWidth: img.clientWidth || undefined,
      renderedHeight: img.clientHeight || undefined,
      transferSizeBytes,
      encodedBodySizeBytes,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      devicePixelRatio: window.devicePixelRatio || 1,
      connection,
    };
  };

  const emit = () => {
    if (reported) return;
    reported = true;
    const report = buildReport();
    window.__heroPerf = report;
    // eslint-disable-next-line no-console
    console.info(`[${label}-perf]`, report);
    window.dispatchEvent(new CustomEvent("hero-perf", { detail: report }));
  };

  // Observe resource timing for the chosen candidate. currentSrc isn't known
  // until the img resolves the srcset, so we match on suffix.
  try {
    resourceObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries() as PerformanceResourceTiming[]) {
        const chosen = img.currentSrc || img.src;
        if (!chosen) continue;
        if (entry.name === chosen || chosen.endsWith(entry.name)) {
          timings.resourceStart = entry.startTime;
          timings.responseEnd = entry.responseEnd;
          transferSizeBytes = entry.transferSize;
          encodedBodySizeBytes = entry.encodedBodySize;
        }
      }
    });
    resourceObserver.observe({ type: "resource", buffered: true });
  } catch {
    /* older browsers */
  }

  // Correlate with the LCP entry if this image is the largest paint.
  try {
    lcpObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries() as any[]) {
        if (entry.element === img) {
          timings.lcp = entry.renderTime || entry.loadTime || entry.startTime;
        }
      }
    });
    lcpObserver.observe({ type: "largest-contentful-paint", buffered: true });
  } catch {
    /* Safari <16 has no LCP observer */
  }

  const onLoad = async () => {
    timings.load = performance.now();
    try {
      if (typeof img.decode === "function") {
        await img.decode();
      }
      timings.decoded = performance.now();
    } catch {
      // decode() can reject when the image is cross-origin without CORS; fall
      // back to the load time so downstream math still works.
      timings.decoded = timings.load;
    }
    // Give the LCP observer one microtask + a rAF to flush its entry so the
    // report includes LCP alongside the load/decode numbers when this is the
    // largest paint.
    requestAnimationFrame(() => {
      idleHandle = window.setTimeout(emit, 50);
    });
  };

  const onError = () => {
    timings.error = performance.now();
    emit();
  };

  // If the image was already cached and loaded synchronously (common on route
  // re-entry), fire the handlers immediately.
  if (img.complete && img.naturalWidth > 0) {
    onLoad();
  } else {
    img.addEventListener("load", onLoad, { once: true });
    img.addEventListener("error", onError, { once: true });
  }

  return () => {
    if (idleHandle != null) window.clearTimeout(idleHandle);
    resourceObserver?.disconnect();
    lcpObserver?.disconnect();
    img.removeEventListener("load", onLoad);
    img.removeEventListener("error", onError);
  };
}
